/**
 * effects.js
 *
 * Motori di animazione condivisi tra view.js (DOM) e edit.js (React).
 * Ogni motore riceve (settings, onTick, onStop) e restituisce una stop().
 *
 * Payload onTick per tipo:
 *   typewriter  : { text: string }
 *   scramble    : { text: string }
 *   stagger     : { chars: Array<{ char, state: 'hidden'|'visible' }> }
 *   slide       : { chars: Array<{ char, state: 'hidden'|'visible' }>, direction: string }
 *   morphblur   : { word: string, state: 'in'|'visible'|'out'|'hidden' }
 */

// ─── Easing ──────────────────────────────────────────────────────────────────

export const EASING = {
	linear: () => 1,
	easeIn: (t) => 0.3 + t * 1.4,
	easeOut: (t) => 1.7 - t * 1.4,
	easeInOut: (t) => (t < 0.5 ? 0.3 + t * 2 * 1.4 : 1.7 - (t - 0.5) * 2 * 1.4),
	bounce: (t) => 1 + Math.sin(t * Math.PI * 3) * 0.4,
};

export function getDelay(baseSpeed, easingName, currentIndex, totalUnits) {
	const fn = EASING[easingName] || EASING.linear;
	const t = totalUnits > 1 ? currentIndex / (totalUnits - 1) : 0;
	return Math.max(10, Math.round(baseSpeed * fn(t)));
}

const SCRAMBLE_CHARS =
	'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&?';
function randomChar() {
	return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
}

// ─── Helper: ciclo parole ─────────────────────────────────────────────────────
// Restituisce la funzione nextWord comune a tutti gli effetti non-typewriter.

function makeWordCycler(words, loop, onStop, animateFn) {
	let wordIndex = 0;

	function nextWord() {
		wordIndex++;
		if (wordIndex >= words.length) {
			if (!loop) {
				onStop();
				return;
			}
			wordIndex = 0;
		}
		animateFn(words[wordIndex], nextWord);
	}

	animateFn(words[wordIndex], nextWord);
}

// ─── Typewriter ───────────────────────────────────────────────────────────────

export function runTypewriter(settings, onTick, onStop) {
	const {
		words,
		animateBy,
		typeSpeed,
		deleteSpeed,
		pauseDuration,
		loop,
		easing,
	} = settings;
	if (!words.length) return () => {};

	const getUnits = (w) => (animateBy === 'word' ? w.split(' ') : [...w]);
	const joinUnits = (u) => (animateBy === 'word' ? u.join(' ') : u.join(''));

	let wordIndex = 0;
	let unitIndex = 0;
	let isDeleting = false;
	let timer = null;
	let stopped = false;

	function tick() {
		if (stopped) return;
		const units = getUnits(words[wordIndex]);
		const total = units.length;

		if (!isDeleting) {
			onTick({ text: joinUnits(units.slice(0, unitIndex + 1)) });
			unitIndex++;
			if (unitIndex > total) {
				isDeleting = true;
				unitIndex = total;
				timer = setTimeout(tick, pauseDuration);
				return;
			}
			timer = setTimeout(
				tick,
				getDelay(typeSpeed, easing, unitIndex - 1, total)
			);
		} else {
			onTick({ text: joinUnits(units.slice(0, unitIndex - 1)) });
			unitIndex--;
			if (unitIndex === 0) {
				isDeleting = false;
				wordIndex++;
				if (wordIndex >= words.length) {
					if (!loop) {
						onStop();
						return;
					}
					wordIndex = 0;
				}
				timer = setTimeout(tick, 100);
				return;
			}
			timer = setTimeout(
				tick,
				getDelay(deleteSpeed, easing, unitIndex - 1, total)
			);
		}
	}

	tick();
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// ─── Scramble ────────────────────────────────────────────────────────────────

export function runScramble(settings, onTick, onStop) {
	const {
		words,
		typeSpeed,
		pauseDuration,
		loop,
		easing,
		scrambleCycles = 8,
	} = settings;
	if (!words.length) return () => {};

	let timer = null;
	let stopped = false;

	function animateWord(word, onDone) {
		if (stopped) return;
		const chars = [...word];
		const total = chars.length;
		const revealed = new Array(total).fill(false);
		let cycle = 0;
		const totalCycles = scrambleCycles + total;

		function frame() {
			if (stopped) return;
			const revealedCount = Math.max(0, cycle - scrambleCycles + 1);
			for (let i = 0; i < total; i++) revealed[i] = i < revealedCount;
			onTick({
				text: chars
					.map((ch, i) => (revealed[i] ? ch : randomChar()))
					.join(''),
			});
			cycle++;
			if (cycle >= totalCycles) {
				onTick({ text: word });
				timer = setTimeout(onDone, pauseDuration);
				return;
			}
			timer = setTimeout(
				frame,
				getDelay(typeSpeed, easing, cycle, totalCycles)
			);
		}
		frame();
	}

	makeWordCycler(words, loop, onStop, (word, next) =>
		animateWord(word, next)
	);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// ─── Stagger Fade ────────────────────────────────────────────────────────────

export function runStagger(settings, onTick, onStop) {
	const { words, typeSpeed, deleteSpeed, pauseDuration, loop, easing } =
		settings;
	if (!words.length) return () => {};

	let timer = null;
	let stopped = false;

	function animateWord(word, onDone) {
		if (stopped) return;
		const chars = [...word];
		const total = chars.length;
		let states = chars.map((ch) => ({ char: ch, state: 'hidden' }));
		onTick({ chars: states });

		let i = 0;
		function fadeIn() {
			if (stopped) return;
			if (i >= total) {
				timer = setTimeout(startFadeOut, pauseDuration);
				return;
			}
			states = states.map((item, idx) =>
				idx === i ? { ...item, state: 'visible' } : item
			);
			onTick({ chars: states });
			i++;
			timer = setTimeout(
				fadeIn,
				getDelay(typeSpeed, easing, i - 1, total)
			);
		}

		function startFadeOut() {
			let j = 0;
			function fadeOut() {
				if (stopped) return;
				states = states.map((item, idx) =>
					idx === j ? { ...item, state: 'hidden' } : item
				);
				onTick({ chars: states });
				j++;
				if (j >= total) {
					timer = setTimeout(onDone, 100);
					return;
				}
				timer = setTimeout(
					fadeOut,
					getDelay(deleteSpeed, easing, j - 1, total)
				);
			}
			fadeOut();
		}

		fadeIn();
	}

	makeWordCycler(words, loop, onStop, animateWord);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// ─── Slide ───────────────────────────────────────────────────────────────────
/**
 * Come stagger ma con traslazione CSS (su/giù/sinistra/destra).
 * Il payload è identico a stagger — la direzione è passata nel payload
 * così il renderer CSS può usare la classe corretta.
 * La transizione visiva è puramente CSS (transform + opacity).
 */
export function runSlide(settings, onTick, onStop) {
	const {
		words,
		typeSpeed,
		deleteSpeed,
		pauseDuration,
		loop,
		easing,
		slideDirection = 'up',
	} = settings;
	if (!words.length) return () => {};

	let timer = null;
	let stopped = false;

	function animateWord(word, onDone) {
		if (stopped) return;
		const chars = [...word];
		const total = chars.length;
		let states = chars.map((ch) => ({ char: ch, state: 'hidden' }));
		onTick({ chars: states, direction: slideDirection });

		let i = 0;
		function slideIn() {
			if (stopped) return;
			if (i >= total) {
				timer = setTimeout(startSlideOut, pauseDuration);
				return;
			}
			states = states.map((item, idx) =>
				idx === i ? { ...item, state: 'visible' } : item
			);
			onTick({ chars: states, direction: slideDirection });
			i++;
			timer = setTimeout(
				slideIn,
				getDelay(typeSpeed, easing, i - 1, total)
			);
		}

		function startSlideOut() {
			let j = 0;
			function slideOut() {
				if (stopped) return;
				states = states.map((item, idx) =>
					idx === j ? { ...item, state: 'hidden' } : item
				);
				onTick({ chars: states, direction: slideDirection });
				j++;
				if (j >= total) {
					timer = setTimeout(onDone, 100);
					return;
				}
				timer = setTimeout(
					slideOut,
					getDelay(deleteSpeed, easing, j - 1, total)
				);
			}
			slideOut();
		}

		slideIn();
	}

	makeWordCycler(words, loop, onStop, animateWord);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// ─── Morph Blur ──────────────────────────────────────────────────────────────
/**
 * La parola intera transisce come blocco unico:
 *   hidden → in (blur alto + opacity 0)
 *   in     → visible (blur 0 + opacity 1)  via CSS transition
 *   visible (pausa)
 *   visible → out (blur alto + opacity 0)
 *   out    → hidden → prossima parola
 *
 * Il payload è { word: string, state: 'hidden'|'in'|'visible'|'out' }.
 * La transizione CSS è definita nello SCSS.
 */
export function runMorphBlur(settings, onTick, onStop) {
	const { words, typeSpeed, pauseDuration, loop, easing } = settings;
	if (!words.length) return () => {};

	// Usiamo typeSpeed come durata della transizione in ms (via CSS var).
	// Qui gestiamo solo i cambi di stato; la transizione visiva è CSS.
	let timer = null;
	let stopped = false;

	// Calcola durata transizione basandosi su typeSpeed (50–300ms)
	const transitionMs = Math.max(200, typeSpeed * 2);

	function animateWord(word, onDone) {
		if (stopped) return;

		// 1. Posiziona la parola hidden (no transition ancora)
		onTick({ word, state: 'hidden' });

		// 2. Dopo un frame, porta a 'in' per triggerare la transizione CSS
		timer = setTimeout(() => {
			if (stopped) return;
			onTick({ word, state: 'in' });

			// 3. Dopo la durata della transizione, la parola è completamente visibile
			timer = setTimeout(() => {
				if (stopped) return;
				onTick({ word, state: 'visible' });

				// 4. Pausa
				timer = setTimeout(() => {
					if (stopped) return;
					onTick({ word, state: 'out' });

					// 5. Aspetta la transizione out poi passa alla prossima
					timer = setTimeout(onDone, transitionMs);
				}, pauseDuration);
			}, transitionMs);
		}, 32); // ~2 frame per garantire che 'hidden' sia stato paintato
	}

	makeWordCycler(words, loop, onStop, animateWord);
	return () => {
		stopped = true;
		clearTimeout(timer);
	};
}

// ─── Dispatcher ──────────────────────────────────────────────────────────────

export function runEffect(effect, settings, onTick, onStop) {
	switch (effect) {
		case 'scramble':
			return runScramble(settings, onTick, onStop);
		case 'stagger':
			return runStagger(settings, onTick, onStop);
		case 'slide':
			return runSlide(settings, onTick, onStop);
		case 'morphblur':
			return runMorphBlur(settings, onTick, onStop);
		default:
			return runTypewriter(settings, onTick, onStop);
	}
}
